import type { AxiosInstance } from "axios";

import { validateResponse } from "api/validateResponse";

import type { GetCommissionRatesArgs, GetPerformanceSummaryArgs, PostCommissionRatesArgs } from "./schemas";
import {
  BaseEnrollmentGoalSchema,
  CommissionRatesResponseSchema,
  type GetActivePatientsArgs,
  GetActivePatientsResponseSchema,
  type GetBillingPortalUrlArgs,
  GetBillingPortalUrlResponseSchema,
  type GetEarningsArgs,
  GetEarningsResponseSchema,
  type GetEnrolledPatientsArgs,
  GetEnrolledPatientsResponseSchema,
  type GetEnrollmentGoalsArgs,
  GetEnrollmentGoalsResponseSchema,
  type GetHealthCareProvidersArgs,
  GetHealthCareProvidersResponseSchema,
  type GetInvoiceSpecificationArgs,
  GetInvoiceSpecificationResponseSchema,
  type GetInvoicesArgs,
  GetInvoicesResponseSchema,
  GetPerformanceSummaryResponseSchema,
  type GetProfessionalsArgs,
  GetProfessionalsResponseSchema,
  type PutEnrollmentGoalArgs,
} from "./schemas";
import {
  type GetPendingTreatmentActivityLogsArgs,
  GetPendingTreatmentActivityLogsResponseSchema,
} from "./schemas/activity_log";
import { type GetUnsignedNotesArgs, GetUnsignedNotesResponseSchema } from "./schemas/unsigned_notes";

export const createAdminService = (transportV2: AxiosInstance) => ({
  async getActivePatients(args: GetActivePatientsArgs) {
    const response = await transportV2.get(
      `/health_care_organizations/${args.health_care_organization_id}/active_patients`,
      {
        params: args.params,
      }
    );
    return validateResponse(response, GetActivePatientsResponseSchema, false);
  },

  async getBillingPortalUrl(args: GetBillingPortalUrlArgs) {
    const response = await transportV2.get(
      `/health_care_organizations/${args.health_care_organization_id}/billing_portal_url`
    );
    return validateResponse(response, GetBillingPortalUrlResponseSchema, false);
  },

  async getCommissionRates(args: GetCommissionRatesArgs) {
    const response = await transportV2.get(
      `/health_care_organizations/${args.health_care_organization_id}/commission_rates`
    );
    return validateResponse(response, CommissionRatesResponseSchema, false);
  },

  async getEarnings(args: GetEarningsArgs) {
    const response = await transportV2.get(`/health_care_organizations/${args.health_care_organization_id}/earnings`, {
      params: args.params,
    });
    return validateResponse(response, GetEarningsResponseSchema, false);
  },

  async getEnrolledPatients(args: GetEnrolledPatientsArgs) {
    const response = await transportV2.get(
      `/health_care_organizations/${args.health_care_organization_id}/enrolled_patients`,
      {
        params: args.params,
      }
    );
    return validateResponse(response, GetEnrolledPatientsResponseSchema, false);
  },

  async getEnrollmentGoals(args: GetEnrollmentGoalsArgs) {
    const response = await transportV2.get(
      `/health_care_organizations/${args.health_care_organization_id}/enrollment_goals`
    );
    return validateResponse(response, GetEnrollmentGoalsResponseSchema, false);
  },

  async getHealthCareProviders(args: GetHealthCareProvidersArgs) {
    const response = await transportV2.get(
      `/health_care_organizations/${args.health_care_organization_id}/health_care_providers`
    );
    return validateResponse(response, GetHealthCareProvidersResponseSchema, false);
  },

  async getInvoices(args: GetInvoicesArgs) {
    const response = await transportV2.get(`/health_care_organizations/${args.health_care_organization_id}/invoices`, {
      params: args.params,
    });
    return validateResponse(response, GetInvoicesResponseSchema, false);
  },

  async getInvoiceSpecification(args: GetInvoiceSpecificationArgs) {
    const response = await transportV2.get(
      `/health_care_organizations/${args.health_care_organization_id}/invoices/${args.invoice_id}/specification`
    );
    return validateResponse(response, GetInvoiceSpecificationResponseSchema, false);
  },

  async getPendingTreatmentActivityLogs(args: GetPendingTreatmentActivityLogsArgs) {
    const response = await transportV2.get(
      `/health_care_organizations/${args.health_care_organization_id}/treatment_activity_logs/pending`,
      {
        params: args.params,
      }
    );
    return validateResponse(response, GetPendingTreatmentActivityLogsResponseSchema, false);
  },

  async getPerformanceSummary(args: GetPerformanceSummaryArgs) {
    const response = await transportV2.get(
      `/health_care_organizations/${args.health_care_organization_id}/performance_summary`,
      {
        params: args.params,
      }
    );
    return validateResponse(response, GetPerformanceSummaryResponseSchema, false);
  },

  async getProfessionals(args: GetProfessionalsArgs) {
    const response = await transportV2.get(
      `/health_care_organizations/${args.health_care_organization_id}/professionals`,
      {
        params: args.params,
      }
    );
    return validateResponse(response, GetProfessionalsResponseSchema, false);
  },

  async getUnsignedNotes(args: GetUnsignedNotesArgs) {
    const response = await transportV2.get(
      `/health_care_organizations/${args.health_care_organization_id}/unsigned_notes`,
      {
        params: args.params,
      }
    );
    return validateResponse(response, GetUnsignedNotesResponseSchema, false);
  },

  async postCommissionRates(args: PostCommissionRatesArgs) {
    const { payload, health_care_organization_id } = args;
    const response = await transportV2.post(
      `/health_care_organizations/${health_care_organization_id}/commission_rates`,
      payload
    );
    return validateResponse(response, CommissionRatesResponseSchema, false);
  },

  async putEnrollmentGoal(args: PutEnrollmentGoalArgs) {
    const { payload, health_care_organization_id } = args;
    const response = await transportV2.put(
      `/health_care_organizations/${health_care_organization_id}/enrollment_goals`,
      payload
    );
    return validateResponse(response, BaseEnrollmentGoalSchema, false);
  },
});

export type AdminService = ReturnType<typeof createAdminService>;
